import { GameFlow } from "./application/game-flow";
import { BotRuntime } from "./application/bot-runtime";
import { BotDefeatRuntime } from "./application/bot-defeat-runtime";
import { CoinRewardFeedbackRuntime } from "./application/coin-reward-feedback";
import { BotRegenerationRuntime } from "./application/bot-regeneration-runtime";
import { CombatRuntime } from "./application/combat-runtime";
import { FirstRunHint } from "./application/first-run-hint";
import { FoodRuntime } from "./application/food-runtime";
import { FreePlayRuntime } from "./application/free-play-runtime";
import { GameplayLifecycle } from "./application/gameplay-lifecycle";
import { HapticRuntime } from "./application/haptic-runtime";
import { PlayerRuntime } from "./application/player-runtime";
import { PlayerDefeatRuntime } from "./application/player-defeat-runtime";
import { RoundRuntime } from "./application/round-runtime";
import { ResultsRuntime } from "./application/results-runtime";
import { ShareRuntime } from "./application/share-runtime";
import {
  ChallengeLinkRouter,
  startChallengeLinkHandling,
} from "./application/challenge-link-runtime";
import { DailyChallengeRuntime } from "./application/daily-challenge-runtime";
import { SeasonalChallengeRuntime } from "./application/seasonal-challenge-runtime";
import { ChallengeRuntime } from "./application/challenge-runtime";
import { GameplaySeedRuntime } from "./application/gameplay-seed-runtime";
import { TailClipRuntime } from "./application/tail-clip-runtime";
import { BonusRuntime } from "./application/bonus-runtime";
import { AudioRuntime } from "./audio/audio-runtime";
import { BrowserAudioBoundary } from "./audio/browser-audio-boundary";
import {
  getFoodValueBenefit,
  getMovementSpeedBenefit,
  getStartingSizeBenefit,
} from "./application/upgrade-rules";
import { buildConfig } from "./config/build-config";
import { startGame } from "./game/start-game";
import { PwaRegistrationCoordinator } from "./pwa/service-worker-registration";
import { serviceWorkerContainerFor } from "./pwa/service-worker-registration";
import { detectRuntimePlatform } from "./platform/runtime-platform";
import { loadRuntimeProgress } from "./platform/native-progress-storage";
import {
  nativeAppPlugin,
  startNativeLifecycle,
} from "./platform/native-lifecycle";
import { CapacitorHapticBoundary } from "./platform/capacitor-haptic-boundary";
import { ShareBoundary } from "./platform/share-boundary";
import { createRandomIdentifier } from "./platform/random-identifier";
import {
  createSnakeSpawnExclusions,
  type SpawnExclusionZone,
} from "./simulation/spawn-exclusions";
import {
  installedVersionLabel,
  readNativeBuildMetadata,
} from "./platform/native-build";
import { installHomeShell } from "./ui/home-shell";
import { installAppStoreLink } from "./ui/app-store-link";
import { installGameplayHud } from "./ui/gameplay-hud";
import { installBonusHud } from "./ui/bonus-hud";
import { installPauseShell } from "./ui/pause-shell";
import { installParentSettingsShell } from "./ui/parent-settings-shell";
import { installPlayerDefeatFeedback } from "./ui/player-defeat-feedback";
import { installCoinRewardFeedback } from "./ui/coin-reward-feedback";
import { installResultsShell } from "./ui/results-shell";
import { installResultsShareShell } from "./ui/results-share-shell";
import { installSettingsTabs } from "./ui/settings-tabs";
import { installSkinShell } from "./ui/skin-shell";
import { installArenaBackgroundShell } from "./ui/arena-background-shell";
import { installUpgradeShell } from "./ui/upgrade-shell";
import { MotionPreference } from "./ui/motion-preference";
import { readSafeAreaInsets } from "./ui/safe-area";
import { seasonalChallengeRegistry } from "./config/seasonal-challenge-registry";
import "./ui/style.css";

export const runtimePlatform = detectRuntimePlatform();
export const appStoreLink = installAppStoreLink(
  document,
  runtimePlatform.isNative,
);
export const nativeBuildMetadata = readNativeBuildMetadata(
  document,
  runtimePlatform,
);
const runtimeProgress = await loadRuntimeProgress(
  runtimePlatform.isNative,
  globalThis,
);
export const progressStore = runtimeProgress.store;
export const pwaRegistration = new PwaRegistrationCoordinator();
void pwaRegistration.start({
  container: serviceWorkerContainerFor(navigator, runtimePlatform.isNative),
  native: runtimePlatform.isNative,
  origin: location.origin,
  productionStyle: import.meta.env.PROD,
  version: buildConfig.version,
});
export const motionPreference = new MotionPreference(
  progressStore,
  document.documentElement,
);
export const gameFlow = new GameFlow();
const createRuntimeIdentifier = (): string =>
  createRandomIdentifier(globalThis.crypto);
const foodSeed = createRuntimeIdentifier();
export const foodRuntime = new FoodRuntime(gameFlow, foodSeed);
export const playerRuntime = new PlayerRuntime(
  gameFlow,
  () => progressStore.getState().selectedSkinId,
  foodRuntime,
  () =>
    getStartingSizeBenefit(progressStore.getState().upgrades["starting-size"]),
  () =>
    getMovementSpeedBenefit(
      progressStore.getState().upgrades["movement-speed"],
    ),
  () => getFoodValueBenefit(progressStore.getState().upgrades["food-value"]),
);
export const roundRuntime = new RoundRuntime(gameFlow, playerRuntime);
const botSeed = createRuntimeIdentifier();
export const botRuntime = new BotRuntime(
  gameFlow,
  botSeed,
  foodRuntime,
  playerRuntime,
);
export const combatRuntime = new CombatRuntime(
  gameFlow,
  playerRuntime,
  botRuntime,
);
const currentSpawnExclusions = (): readonly SpawnExclusionZone[] => {
  const playerSnapshot = playerRuntime.getSnapshot();
  const player = playerSnapshot.player;
  const playerSnakes =
    player?.alive === true
      ? [
          {
            body: playerSnapshot.body?.segments ?? [],
            position: player.position,
            radius: player.collision.radius,
          },
        ]
      : [];
  const botSnakes = botRuntime.getSnapshot().bots.map((bot) => ({
    body: bot.body.segments,
    position: bot.position,
    radius: bot.collision.radius,
  }));
  return createSnakeSpawnExclusions([...playerSnakes, ...botSnakes]);
};
foodRuntime.setSpawnExclusionSource(currentSpawnExclusions);
const bonusSeed = createRuntimeIdentifier();
export const bonusRuntime = new BonusRuntime(
  gameFlow,
  playerRuntime,
  bonusSeed,
  currentSpawnExclusions,
);
const botDefeatSeed = createRuntimeIdentifier();
export const botDefeatRuntime = new BotDefeatRuntime(
  gameFlow,
  combatRuntime,
  playerRuntime,
  botRuntime,
  foodRuntime,
  botDefeatSeed,
);
export const botRegenerationRuntime = new BotRegenerationRuntime(
  gameFlow,
  botDefeatRuntime,
  playerRuntime,
  botRuntime,
);
export const coinRewardFeedbackRuntime = new CoinRewardFeedbackRuntime(
  gameFlow,
  botDefeatRuntime,
  playerRuntime,
);
const playerDefeatSeed = createRuntimeIdentifier();
export const playerDefeatRuntime = new PlayerDefeatRuntime(
  gameFlow,
  combatRuntime,
  playerRuntime,
  botRuntime,
  playerDefeatSeed,
);
export const gameplaySeedRuntime = new GameplaySeedRuntime([
  { normalSeed: foodSeed, source: foodRuntime },
  { normalSeed: botSeed, source: botRuntime },
  { normalSeed: bonusSeed, source: bonusRuntime },
  { normalSeed: botDefeatSeed, source: botDefeatRuntime },
  { normalSeed: playerDefeatSeed, source: playerDefeatRuntime },
]);
export const dailyChallengeRuntime = new DailyChallengeRuntime(
  gameFlow,
  progressStore,
  gameplaySeedRuntime,
  Date.now,
);
export const seasonalChallengeRuntime = new SeasonalChallengeRuntime(
  gameFlow,
  progressStore,
  gameplaySeedRuntime,
  seasonalChallengeRegistry,
  Date.now,
);
export const challengeRuntime = new ChallengeRuntime(
  dailyChallengeRuntime,
  seasonalChallengeRuntime,
);
export const tailClipRuntime = new TailClipRuntime(
  gameFlow,
  playerRuntime,
  botRuntime,
  botDefeatRuntime,
  playerDefeatRuntime,
);
export const freePlayRuntime = new FreePlayRuntime(
  gameFlow,
  botDefeatRuntime,
  playerRuntime,
  progressStore,
  createRuntimeIdentifier,
);
export const resultsRuntime = new ResultsRuntime(
  gameFlow,
  roundRuntime,
  playerRuntime,
  botDefeatRuntime,
  progressStore,
  createRuntimeIdentifier,
  challengeRuntime,
);
export const shareRuntime = new ShareRuntime(
  resultsRuntime,
  new ShareBoundary(runtimePlatform.isNative),
);
export const firstRunHint = new FirstRunHint(
  gameFlow,
  progressStore,
  playerRuntime,
);
export const gameplayLifecycle = new GameplayLifecycle(
  gameFlow,
  playerRuntime,
  progressStore,
  document,
  freePlayRuntime,
);
export const nativeLifecycle = startNativeLifecycle(
  runtimePlatform.isNative,
  gameplayLifecycle,
  runtimeProgress.flush,
);
export const audioRuntime = new AudioRuntime({
  botDefeatRuntime,
  bonusRuntime,
  boundary: new BrowserAudioBoundary(),
  flow: gameFlow,
  gestureSource: document,
  lifecycle: gameplayLifecycle,
  playerDefeatRuntime,
  playerRuntime,
  preferences: progressStore,
  resultsRuntime,
  tailClipRuntime,
});
export const hapticRuntime = new HapticRuntime({
  bonusRuntime,
  botDefeatRuntime,
  boundary: new CapacitorHapticBoundary(runtimePlatform.isNative),
  now: Date.now,
  preferenceSource: progressStore,
  playerDefeatRuntime,
  playerRuntime,
  resultsRuntime,
});
export const syncPreferences = (): void => {
  audioRuntime.syncPreferences();
  motionPreference.sync();
};
export const homeShell = installHomeShell(
  document,
  gameFlow,
  dailyChallengeRuntime,
  seasonalChallengeRuntime,
  progressStore,
);
export const skinShell = installSkinShell(document, progressStore);
export const arenaBackgroundShell = installArenaBackgroundShell(
  document,
  progressStore,
);
export const upgradeShell = installUpgradeShell(
  document,
  gameFlow,
  progressStore,
  () => {
    skinShell.render();
  },
);
export const pauseShell = installPauseShell(document, gameplayLifecycle);
export const parentSettingsShell = installParentSettingsShell(
  document,
  progressStore,
  installedVersionLabel(buildConfig.versionLabel, nativeBuildMetadata),
  syncPreferences,
);
export const settingsTabs = installSettingsTabs(
  document,
  () => {
    upgradeShell.render();
    arenaBackgroundShell.render();
  },
  () => {
    parentSettingsShell.render();
  },
  () => {
    parentSettingsShell.close();
  },
);
export const gameplayHud = installGameplayHud(
  document,
  gameFlow,
  roundRuntime,
  firstRunHint,
);
export const bonusHud = installBonusHud(
  document,
  gameplayLifecycle,
  bonusRuntime,
);
export const playerDefeatFeedback = installPlayerDefeatFeedback(
  document,
  playerDefeatRuntime,
);
export const coinRewardFeedback = installCoinRewardFeedback(
  document,
  coinRewardFeedbackRuntime,
);
export const resultsShell = installResultsShell(document, resultsRuntime);
export const resultsShareShell = installResultsShareShell(
  document,
  shareRuntime,
);
export const getSelectedArenaBackgroundId = () =>
  progressStore.getState().selectedArenaBackgroundId;
export const getSafeAreaInsets = readSafeAreaInsets.bind(undefined, document);
export const getHighContrastPreference = (): boolean =>
  globalThis.matchMedia("(prefers-contrast: more)").matches;
export const getSeasonalArenaDescriptor = () =>
  seasonalChallengeRuntime.getArenaDescriptor();
export const game = startGame(
  gameFlow,
  playerRuntime,
  foodRuntime,
  botRuntime,
  firstRunHint,
  motionPreference.getProfile.bind(motionPreference),
  bonusRuntime,
  tailClipRuntime,
  coinRewardFeedbackRuntime,
  getSelectedArenaBackgroundId,
  getSafeAreaInsets,
  getSeasonalArenaDescriptor,
  getHighContrastPreference,
);
export const challengeLinkRouter = new ChallengeLinkRouter(gameFlow, {
  startDaily: (identity) => dailyChallengeRuntime.startIdentity(identity),
  startSeasonal: (identity) => seasonalChallengeRuntime.startIdentity(identity),
});
export const nativeChallengeLinks = startChallengeLinkHandling(
  runtimePlatform.isNative,
  challengeLinkRouter,
  nativeAppPlugin,
  globalThis.location.href,
);
